import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2, ShieldCheck, Wallet } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import useRazorpay from '../../hooks/useRazorpay';
import CommonToaster from '../Common/CommonToaster';
import { AppModal } from '../ui';
import { convertToIndianCurrency } from '../../utils/common';
import '../Fees/payment.scss';

const QUICK_PRESETS = [
  { key: 'quarter', label: '25%', ratio: 0.25 },
  { key: 'half', label: '50%', ratio: 0.5 },
  { key: 'full', label: 'Full', ratio: 1 },
];

const clampAmount = (value, max) => {
  const n = Math.round(parseFloat(value) || 0);
  if (n <= 0) return '';
  return String(Math.min(n, max));
};

/**
 * StudentSelfPay — Self-payment modal for students (profile, navbar, dashboard).
 */
const StudentSelfPay = ({
  student,
  courseAssignmentId = null,
  pendingFees: pendingFeesOverride = null,
  courseName = '',
  defaultAmount = null,
  show = false,
  onHide,
  onPaymentSuccess,
}) => {
  const [amount, setAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [rzpEnabled, setRzpEnabled] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const pending = pendingFeesOverride != null
    ? parseFloat(pendingFeesOverride) || 0
    : parseFloat(student?.pendingFees) || 0;

  useEffect(() => {
    apiClient
      .get('/payments/config')
      .then(({ data }) => setRzpEnabled(!!data.enabled))
      .catch(() => setRzpEnabled(false))
      .finally(() => setConfigLoading(false));
  }, []);

  useEffect(() => {
    // Prefill with the requested amount (e.g. the next EMI's balance), capped to
    // the pending balance; otherwise start blank.
    if (show) setAmount(defaultAmount ? clampAmount(defaultAmount, pending) : '');
  }, [show, pending, courseAssignmentId, defaultAmount]);

  const handlePay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setToast({ show: true, message: 'Enter a valid amount.', type: 'error' });
      return;
    }
    if (amt > pending) {
      setToast({ show: true, message: `Amount cannot exceed ₹${pending}.`, type: 'error' });
      return;
    }

    setProcessing(true);
    try {
      const { data: orderData } = await apiClient.post('/payments/create-order', {
        studentId: student._id,
        amount: amt,
        description: 'Fee Payment (Self)',
        ...(courseAssignmentId ? { courseAssignmentId } : {}),
      });

      openCheckout(
        orderData,
        async (response) => {
          try {
            await apiClient.post('/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              transactionId: orderData.transactionId,
            });
            setToast({ show: true, message: 'Payment successful! Receipt will be emailed.', type: 'success' });
            if (onHide) onHide();
            setAmount('');
            if (onPaymentSuccess) onPaymentSuccess();
          } catch {
            setToast({ show: true, message: 'Payment received but verification failed. Contact admin.', type: 'error' });
          }
          setProcessing(false);
        },
        (error) => {
          setToast({ show: true, message: error?.reason || 'Payment cancelled.', type: 'error' });
          setProcessing(false);
        },
      );
    } catch (ex) {
      const msg = ex?.response?.data?.message || 'Could not initiate payment.';
      setToast({ show: true, message: msg, type: 'error' });
      setProcessing(false);
    }
  };

  if (configLoading) return null;
  if (!rzpEnabled || pending <= 0) return null;

  const payingNow = parseFloat(amount) || 0;
  const dueAfter = Math.max(pending - payingNow, 0);
  const isBusy = processing || rzpLoading;
  const activePreset = QUICK_PRESETS.find((p) => {
    if (!payingNow) return false;
    const target = p.ratio === 1 ? pending : Math.max(1, Math.round(pending * p.ratio));
    return payingNow === target;
  })?.key;

  const setPreset = (ratio) => {
    const value = ratio === 1 ? pending : Math.max(1, Math.round(pending * ratio));
    setAmount(String(value));
  };

  const footer = (
    <>
      <button
        type="button"
        className="pay-modal-v2__cancel"
        onClick={onHide}
        disabled={isBusy}
      >
        Cancel
      </button>
      <button
        type="button"
        className="pay-modal-v2__submit"
        onClick={handlePay}
        disabled={isBusy || !amount}
      >
        {isBusy ? (
          <>
            <Loader2 size={16} className="pay-modal-v2__spin" />
            Processing…
          </>
        ) : (
          <>
            <CreditCard size={16} />
            Pay {convertToIndianCurrency(payingNow)}
          </>
        )}
      </button>
    </>
  );

  return (
    <>
      <AppModal
        show={show}
        onHide={onHide}
        size="sm"
        className="pay-modal-v2"
        bodyClassName="pay-modal-v2__body"
        title="Pay Online"
        icon={(
          <span className="pay-modal-v2__head-icon" aria-hidden="true">
            <Wallet size={18} strokeWidth={2.2} />
          </span>
        )}
        footer={footer}
      >
        <div className="pay-modal-v2__hero">
          <span className="pay-modal-v2__hero-label">Pending balance</span>
          <span className="pay-modal-v2__hero-value">{convertToIndianCurrency(pending)}</span>
          {courseName && (
            <span className="pay-modal-v2__hero-course">{courseName}</span>
          )}
        </div>

        <div className="pay-modal-v2__quick">
          <span className="pay-modal-v2__quick-label">Quick select</span>
          <div className="pay-modal-v2__quick-btns">
            {QUICK_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={`pay-modal-v2__quick-btn${activePreset === preset.key ? ' is-active' : ''}`}
                onClick={() => setPreset(preset.ratio)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div className="pay-modal-v2__field">
          <label htmlFor="pay-amount">Amount to pay</label>
          <div className="pay-modal-v2__input-wrap">
            <span className="pay-modal-v2__input-prefix">₹</span>
            <input
              id="pay-amount"
              type="number"
              min={1}
              max={pending}
              step={1}
              value={amount}
              onChange={(e) => setAmount(clampAmount(e.target.value, pending))}
              placeholder="Enter amount"
              inputMode="numeric"
            />
          </div>
          <span className="pay-modal-v2__hint">
            Min ₹1 · Max {convertToIndianCurrency(pending)}
          </span>
        </div>

        <div className="pay-modal-v2__summary">
          <div className="pay-modal-v2__summary-row">
            <span>Current pending</span>
            <strong>{convertToIndianCurrency(pending)}</strong>
          </div>
          <div className="pay-modal-v2__summary-row">
            <span>Paying now</span>
            <strong className="pay-modal-v2__summary-accent">
              {convertToIndianCurrency(payingNow)}
            </strong>
          </div>
          <div className="pay-modal-v2__summary-row pay-modal-v2__summary-row--due">
            <span>Due after payment</span>
            <strong>{convertToIndianCurrency(dueAfter)}</strong>
          </div>
        </div>

        <p className="pay-modal-v2__secure">
          <ShieldCheck size={14} />
          Secured checkout via Razorpay
        </p>
      </AppModal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </>
  );
};

export default StudentSelfPay;
